import { useCallback, useRef } from 'react'
import type { LessonCallQualityStats, WebrtcIceServer } from '@/api/lessonCalls'
import type { CallSignalMessage, CallSignalPayload } from './callSignaling'

/**
 * Mesh из RTCPeerConnection: соединение к каждому участнику звонка.
 *
 * Кто кому шлёт offer, не согласовывается заранее — используется «perfect
 * negotiation»: обе стороны инициируют одновременно, а роль polite/impolite
 * (детерминированно, по user id) решает, кто уступает при столкновении. Иначе
 * пришлось бы выбирать инициатора, и гонка при одновременном входе двух
 * участников оставляла бы соединение наполовину установленным.
 */

type PeerEntry = {
  connection: RTCPeerConnection
  polite: boolean
  makingOffer: boolean
  ignoreOffer: boolean
  settingRemoteAnswer: boolean
  /** Число разрывов — попадает в сводку качества связи. */
  disconnects: number
}

export type PeerConnectionsHandlers = {
  selfId: number
  /**
   * Геттер, а не значение: конфиг приходит с сервера асинхронно, и соединение,
   * созданное до его загрузки, осталось бы вовсе без TURN.
   */
  getIceServers: () => WebrtcIceServer[]
  /**
   * Потолок битрейта видео из тарифа: без него браузер сам разгоняется до
   * своего максимума, и тариф «sd» ничем не отличался бы от «hd».
   */
  getVideoMaxBitrate: () => number | null
  getLocalStream: () => MediaStream | null
  sendSignal: (message: CallSignalMessage) => void
  onRemoteStream: (userId: number, stream: MediaStream) => void
  onPeerClosed: (userId: number) => void
  onConnectionStateChange?: (userId: number, state: RTCPeerConnectionState) => void
}

function toRtcIceServers(servers: WebrtcIceServer[]): RTCIceServer[] {
  return servers.map((server) => ({
    urls: server.urls,
    ...(server.username ? { username: server.username } : {}),
    ...(server.credential ? { credential: server.credential } : {}),
  }))
}

export function usePeerConnections(handlers: PeerConnectionsHandlers) {
  const peersRef = useRef<Map<number, PeerEntry>>(new Map())
  /**
   * Sender-ы, через которые уходит видео. После `replaceTrack(null)` у sender-а
   * нет дорожки, и по `sender.track.kind` его уже не найти — а переиспользовать
   * нужно именно его.
   */
  const videoSendersRef = useRef<Set<RTCRtpSender>>(new Set())
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  const closePeer = useCallback((userId: number) => {
    const entry = peersRef.current.get(userId)
    if (!entry) return

    entry.connection.onicecandidate = null
    entry.connection.ontrack = null
    entry.connection.onnegotiationneeded = null
    entry.connection.onconnectionstatechange = null
    entry.connection.getSenders().forEach((sender) => videoSendersRef.current.delete(sender))
    entry.connection.close()
    peersRef.current.delete(userId)

    handlersRef.current.onPeerClosed(userId)
  }, [])

  const ensurePeer = useCallback((userId: number): PeerEntry => {
    const existing = peersRef.current.get(userId)
    if (existing) return existing

    const { selfId } = handlersRef.current
    const connection = new RTCPeerConnection({
      iceServers: toRtcIceServers(handlersRef.current.getIceServers()),
    })

    const entry: PeerEntry = {
      connection,
      // Детерминированное распределение ролей: у обеих сторон получается
      // противоположное значение без обмена сообщениями.
      polite: selfId < userId,
      makingOffer: false,
      ignoreOffer: false,
      settingRemoteAnswer: false,
      disconnects: 0,
    }
    peersRef.current.set(userId, entry)

    const localStream = handlersRef.current.getLocalStream()
    const maxBitrate = handlersRef.current.getVideoMaxBitrate()

    localStream?.getTracks().forEach((track) => {
      const sender = connection.addTrack(track, localStream)

      if (track.kind !== 'video') return

      videoSendersRef.current.add(sender)

      if (maxBitrate) {
        const parameters = sender.getParameters()
        if (!parameters.encodings || parameters.encodings.length === 0) {
          parameters.encodings = [{}]
        }
        parameters.encodings[0].maxBitrate = maxBitrate
        void sender.setParameters(parameters).catch(() => undefined)
      }
    })

    connection.onicecandidate = (event) => {
      if (!event.candidate) return
      handlersRef.current.sendSignal({
        from: selfId,
        to: userId,
        payload: { kind: 'candidate', candidate: event.candidate.toJSON() },
      })
    }

    connection.ontrack = (event) => {
      const stream = event.streams[0]
      if (stream) {
        handlersRef.current.onRemoteStream(userId, stream)
      }
    }

    connection.onnegotiationneeded = async () => {
      try {
        entry.makingOffer = true
        await connection.setLocalDescription()
        const description = connection.localDescription
        if (!description) return

        handlersRef.current.sendSignal({
          from: selfId,
          to: userId,
          payload: { kind: 'offer', sdp: description.sdp },
        })
      } catch (error) {
        console.error('[call] Не удалось создать offer', error)
      } finally {
        entry.makingOffer = false
      }
    }

    connection.onconnectionstatechange = () => {
      handlersRef.current.onConnectionStateChange?.(userId, connection.connectionState)

      if (connection.connectionState === 'failed') {
        entry.disconnects += 1
        // Смена сети (Wi-Fi → LTE) обрывает ICE: перебираем кандидатов заново,
        // не пересоздавая соединение и не теряя звонок.
        if (!entry.polite) {
          connection.restartIce()
        }
      }
    }

    return entry
  }, [])

  /** Установить соединение с участником (вызывается при появлении его в канале). */
  const connectTo = useCallback(
    (userId: number) => {
      if (userId === handlersRef.current.selfId) return
      ensurePeer(userId)
    },
    [ensurePeer],
  )

  const handleSignal = useCallback(
    async (message: CallSignalMessage) => {
      const { selfId } = handlersRef.current
      if (message.to !== selfId || message.from === selfId) return

      const entry = ensurePeer(message.from)
      const { connection } = entry
      const payload: CallSignalPayload = message.payload

      try {
        if (payload.kind === 'candidate') {
          try {
            await connection.addIceCandidate(payload.candidate)
          } catch (error) {
            // Кандидаты отклонённого offer-а приходить не перестанут — молча
            // игнорируем их, но настоящую ошибку показываем.
            if (!entry.ignoreOffer) throw error
          }
          return
        }

        if (payload.kind === 'offer') {
          const readyForOffer =
            !entry.makingOffer &&
            (connection.signalingState === 'stable' || entry.settingRemoteAnswer)
          const offerCollision = !readyForOffer

          entry.ignoreOffer = !entry.polite && offerCollision
          if (entry.ignoreOffer) return

          await connection.setRemoteDescription({ type: 'offer', sdp: payload.sdp })
          await connection.setLocalDescription()

          const description = connection.localDescription
          if (!description) return

          handlersRef.current.sendSignal({
            from: selfId,
            to: message.from,
            payload: { kind: 'answer', sdp: description.sdp },
          })
          return
        }

        entry.settingRemoteAnswer = true
        await connection.setRemoteDescription({ type: 'answer', sdp: payload.sdp })
        entry.settingRemoteAnswer = false
      } catch (error) {
        entry.settingRemoteAnswer = false
        console.error('[call] Ошибка обработки сигнала', error)
      }
    },
    [ensurePeer],
  )

  /**
   * Отдать видеодорожку во все соединения.
   *
   * Если видео-sender уже есть — заменяем дорожку в нём: `addTrack` создал бы
   * ещё одну m-секцию в SDP, и каждый цикл «включил/выключил камеру» удлинял
   * бы описание сессии. Если sender-а нет (звонок начинался как аудио) —
   * добавляем впервые, иначе дорожка никуда не уходит и собеседник ничего
   * не видит.
   */
  const publishVideoTrack = useCallback(
    async (track: MediaStreamTrack, stream: MediaStream) => {
      const maxBitrate = handlersRef.current.getVideoMaxBitrate()
      const tasks: Promise<void>[] = []

      peersRef.current.forEach((entry) => {
        const videoSender = entry.connection
          .getSenders()
          .find((sender) => sender.track?.kind === 'video' || videoSendersRef.current.has(sender))

        const sender = videoSender ?? entry.connection.addTrack(track, stream)
        videoSendersRef.current.add(sender)

        if (videoSender) {
          tasks.push(sender.replaceTrack(track))
        }

        if (maxBitrate) {
          const parameters = sender.getParameters()
          if (!parameters.encodings || parameters.encodings.length === 0) {
            parameters.encodings = [{}]
          }
          parameters.encodings[0].maxBitrate = maxBitrate
          tasks.push(sender.setParameters(parameters))
        }
      })

      await Promise.all(tasks)
    },
    [],
  )

  /** Заменить или убрать исходящую видеодорожку (демонстрация экрана, камера). */
  const replaceVideoTrack = useCallback(async (track: MediaStreamTrack | null) => {
    const tasks: Promise<void>[] = []

    peersRef.current.forEach((entry) => {
      entry.connection.getSenders().forEach((sender) => {
        if (sender.track?.kind === 'video' || videoSendersRef.current.has(sender)) {
          tasks.push(sender.replaceTrack(track))
        }
      })
    })

    await Promise.all(tasks)
  }, [])

  /**
   * Сводка качества связи по всем соединениям — отправляется при выходе.
   * Без неё поддержка не может отличить «плохой интернет у ученика» от
   * проблемы с TURN.
   */
  const collectQualityStats = useCallback(async (): Promise<LessonCallQualityStats | undefined> => {
    const peers = Array.from(peersRef.current.values())
    if (peers.length === 0) return undefined

    let rttSum = 0
    let rttCount = 0
    let jitterSum = 0
    let jitterCount = 0
    let packetsLost = 0
    let packetsReceived = 0
    let relayCount = 0
    let pairCount = 0
    let disconnects = 0

    for (const entry of peers) {
      disconnects += entry.disconnects

      let report: RTCStatsReport
      try {
        report = await entry.connection.getStats()
      } catch {
        continue
      }

      const candidates = new Map<string, { candidateType?: string }>()
      report.forEach((stat) => {
        if (stat.type === 'local-candidate' || stat.type === 'remote-candidate') {
          candidates.set(stat.id, stat as { candidateType?: string })
        }
      })

      report.forEach((stat) => {
        if (stat.type === 'candidate-pair' && stat.state === 'succeeded') {
          pairCount += 1
          if (typeof stat.currentRoundTripTime === 'number') {
            rttSum += stat.currentRoundTripTime * 1000
            rttCount += 1
          }
          const local = candidates.get(String(stat.localCandidateId))
          if (local?.candidateType === 'relay') relayCount += 1
        }

        if (stat.type === 'inbound-rtp' && !stat.isRemote) {
          if (typeof stat.jitter === 'number') {
            jitterSum += stat.jitter * 1000
            jitterCount += 1
          }
          if (typeof stat.packetsLost === 'number') packetsLost += stat.packetsLost
          if (typeof stat.packetsReceived === 'number') packetsReceived += stat.packetsReceived
        }
      })
    }

    const totalPackets = packetsLost + packetsReceived
    const round = (value: number) => Math.round(value * 100) / 100

    return {
      packet_loss: totalPackets > 0 ? round((packetsLost / totalPackets) * 100) : 0,
      jitter_ms: jitterCount > 0 ? round(jitterSum / jitterCount) : 0,
      rtt_ms: rttCount > 0 ? round(rttSum / rttCount) : 0,
      relay_share: pairCount > 0 ? round(relayCount / pairCount) : 0,
      disconnects,
    }
  }, [])

  const closeAll = useCallback(() => {
    Array.from(peersRef.current.keys()).forEach((userId) => closePeer(userId))
  }, [closePeer])

  return {
    connectTo,
    handleSignal,
    closePeer,
    closeAll,
    publishVideoTrack,
    replaceVideoTrack,
    collectQualityStats,
  }
}
